import React, { useRef, useState, useContext } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import DeleteButton from '../Generic/DeleteButton';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import ProductDetailModal from '../Product/ProductDetailModal';
import NotificationDetailModal from './NotificationDetailModal';
import { showToast } from '../../config_and_helpers/helpers';
import {
  deleteOrderNotification,
  getOrderNotificationDetails,
} from '../../clients/notifications_client';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { logError } from '../../config_and_helpers/logger';
import {
  formatNotificationUpdatedAt,
  getNotificationReasonBadgeVariant,
  getNotificationReasonLabel,
} from '../../utils/notificationReason';

/**
 * Table of order notifications with reason badges and lazy-loaded statistics.
 */
const NotificationsTable = ({
  notificationsList,
  handleEdit,
  onDeleteSuccess,
  clearSearchValue,
}) => {
  const [fetchedSuppliers, setFetchedSuppliers] = useState([]);
  const fetchedSuppliersRef = useRef(new Set());
  const [detailedData, setDetailedData] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const loadNotificationDetails = async (notificationId, { silent = false } = {}) => {
    if (detailedData[notificationId]) {
      return detailedData[notificationId];
    }
    if (loadingDetails[notificationId]) {
      return null;
    }

    setLoadingDetails(prev => ({ ...prev, [notificationId]: true }));

    try {
      const result = await getOrderNotificationDetails(token, notificationId);

      if (result.success) {
        setDetailedData(prev => ({ ...prev, [notificationId]: result.data }));
        return result.data;
      }

      if (!silent) {
        showToast(
          'Failed to load notification details',
          'error',
          'top-right',
          3000
        );
      }
      return null;
    } catch (error) {
      logError('NotificationsTable', 'loadNotificationDetails failed', error);
      if (!silent) {
        showToast(
          'Failed to load notification details',
          'error',
          'top-right',
          3000
        );
      }
      return null;
    } finally {
      setLoadingDetails(prev => ({ ...prev, [notificationId]: false }));
    }
  };

  const handleHover = notificationId => {
    loadNotificationDetails(notificationId, { silent: true });
  };

  const handleRowClick = notification => {
    setSelectedNotification(notification);
    setShowDetailModal(true);
    loadNotificationDetails(notification.id);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedNotification(null);
  };

  const getDetailedData = notificationId => detailedData[notificationId] || null;

  const isDetailsLoading = notificationId => loadingDetails[notificationId] || false;

  const renderStatCell = (notificationId, statKey) => {
    if (isDetailsLoading(notificationId)) {
      return <span className="text-muted">Loading...</span>;
    }
    const stats = getDetailedData(notificationId)?.product?.statistics;
    const value = stats?.[statKey];
    return value ?? '—';
  };

  const handleSuccessfulDelete = notificationId => {
    onDeleteSuccess?.(notificationId);
    handleEdit?.();
  };

  return (
    <>
      <Table striped bordered hover>
        <thead>
          <tr className="text-center">
            <th>#</th>
            <th>Reason</th>
            <th>Updated</th>
            <th>Supplier</th>
            <th>Product</th>
            <th>Last Ordered</th>
            <th>Average Order Interval</th>
            <th>Current Stock</th>
            <th>Average Order Quantity</th>
            {!isViewer ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {notificationsList.map((notification, index) => {
            const fetchedSupplierObj = fetchedSuppliers.find(
              supplier => supplier?.id === notification?.product?.supplier_id
            );

            return (
              <tr
                key={notification.id}
                className="text-center align-middle"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => handleHover(notification.id)}
                onClick={() => handleRowClick(notification)}
              >
                <td>{index + 1}</td>
                <td>
                  <Badge bg={getNotificationReasonBadgeVariant(notification.reason)}>
                    {getNotificationReasonLabel(notification.reason)}
                  </Badge>
                </td>
                <td>{formatNotificationUpdatedAt(notification.updated_at)}</td>
                <td onClick={e => e.stopPropagation()}>
                  <SupplierDetailModal
                    fetchingSuppliersRef={fetchedSuppliersRef}
                    fetchedSuppliers={fetchedSuppliers}
                    setFetchedSuppliers={setFetchedSuppliers}
                    supplierObj={
                      fetchedSupplierObj || {
                        id: notification.product.supplier_id,
                        name: notification.product.supplier_name,
                      }
                    }
                    supplierId={notification.product.supplier_id}
                    smallerFont={true}
                  />
                </td>
                <td onClick={e => e.stopPropagation()}>
                  {isDetailsLoading(notification.id) ? (
                    <span className="text-muted">Loading...</span>
                  ) : (
                    <ProductDetailModal
                      productObj={
                        getDetailedData(notification.id)?.product ||
                        notification.product
                      }
                    />
                  )}
                </td>
                <td>{renderStatCell(notification.id, 'last_ordered')}</td>
                <td>{renderStatCell(notification.id, 'avg_order_time')}</td>
                <td>{notification.product?.current_stock ?? '—'}</td>
                <td>{renderStatCell(notification.id, 'avg_order_quantity')}</td>
                {!isViewer ? (
                <td
                  className="d-flex flex-row align-items-center justify-content-evenly"
                  onClick={e => e.stopPropagation()}
                >
                  <DeleteButton
                    objectType="order notification"
                    objectName={notification.id}
                    objectId={notification.id}
                    deleteFetchFunc={deleteOrderNotification}
                    onSuccessfulDelete={() => handleSuccessfulDelete(notification.id)}
                    clearSearchValue={clearSearchValue}
                  />
                </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </Table>

      <NotificationDetailModal
        show={showDetailModal}
        onHide={handleCloseDetailModal}
        notification={selectedNotification}
        detail={
          selectedNotification
            ? getDetailedData(selectedNotification.id)
            : null
        }
        loading={
          selectedNotification
            ? isDetailsLoading(selectedNotification.id)
            : false
        }
      />
    </>
  );
};

export default NotificationsTable;
